import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { Partner } from '../../_generated';
import { PartnerService } from '../api/partner.service';

/**
 * Angular route resolver that ensures partner data is available before route activation.
 * This resolver attempts to retrieve the currently logged-in partner information in the following order:
 * 1. First checks if partner data already exists in the PartnerService cache
 * 2. If no cached data exists, makes an API call to fetch the partner data
 *
 * Use this resolver in route configurations where partner data is required before
 * the route can be activated. This prevents components from loading without
 * necessary partner context.
 *
 * @example
 * ```typescript
 * const routes: Routes = [{
 *   path: 'partner-dashboard',
 *   component: DashboardComponent,
 *   resolve: { partner: partnerResolver }
 * }];
 * ```
 *
 * @returns Promise<Partner> A promise that resolves with the partner data
 */
export const partnerResolver: ResolveFn<Partner> = async () => {
    const partnerService = inject(PartnerService);

    // Try to get existing partner first
    const partner = partnerService.partner();

    // If no existing partner, fetch from API
    if (!partner) {
        return await lastValueFrom(partnerService.getLoggedInPartner());
    }

    return partner;
};
